import { z } from 'zod';
import { D } from '../decimal.js';
import {
  AccountCode,
  CcyCodeSchema,
  IsoDate,
  MoneyStr,
  OperatorUsername,
  OrderStatusSchema,
  PositiveMoney,
  ProvodkaStatusSchema,
  StatusFlagSchema,
} from './common.js';

export const Provodka = z.object({
  id: z.string().regex(/^TX-\d+$/),
  orderId: z.string().regex(/^W-\d+$/),
  idx: z.number().int().min(0).max(3),
  type: z.literal('obligation'),
  account: AccountCode,
  accountLabel: z.enum(['Клиент', 'Исполнитель']),
  ccy: CcyCodeSchema,
  amount: MoneyStr,
  actionLabel: z.string(),
  status: ProvodkaStatusSchema,
  date: IsoDate,
  confirmedAt: z.string().nullable(),
  confirmedBy: z.string().uuid().nullable(),
});
export type Provodka = z.infer<typeof Provodka>;

export const ReverseEntry = z.object({
  id: z.string().regex(/^TX-\d+$/),
  orderId: z.string().regex(/^W-\d+$/),
  provId: z.string().regex(/^TX-\d+$/),
  provIndex: z.number().int().min(0).max(3),
  type: z.literal('execution'),
  account: AccountCode,
  accountLabel: z.enum(['Клиент', 'Исполнитель']),
  ccy: CcyCodeSchema,
  amount: MoneyStr,
  status: z.literal('done'),
  date: IsoDate,
});
export type ReverseEntry = z.infer<typeof ReverseEntry>;

export const OrderListItem = z.object({
  id: z.string().regex(/^W-\d+$/),
  client: AccountCode,
  executor: AccountCode,
  clGiveCcy: CcyCodeSchema,
  clGiveAmt: MoneyStr,
  clGetCcy: CcyCodeSchema,
  clGetAmt: MoneyStr,
  operator: OperatorUsername,
  date: IsoDate,
  statusFlag: StatusFlagSchema,
  status: OrderStatusSchema,
  progress: z.number().int().min(0).max(4),
  /**
   * Профит ВСЕГДА в USDT (universal-USDT-модель). signed.
   */
  profitAmount: MoneyStr,
  profitCcy: z.literal('USDT'),
  /**
   * Точное значение профита в исходной валюте — заполнено только для
   * same-currency сделок (clGiveCcy === exGetCcy ИЛИ clGetCcy === exGiveCcy).
   * UI может показать рядом с USDT-значением для большей точности.
   */
  profitNativeAmount: MoneyStr.nullable(),
  profitNativeCcy: CcyCodeSchema.nullable(),
  rejectReason: z.string().nullable(),
});
export type OrderListItem = z.infer<typeof OrderListItem>;

/**
 * Коэффициент — наценка/скидка на сумму клиента, транзитом передаваемая
 * приведшему клиента (анонимный реферер). Процент, применяется к clGive
 * ПОСЛЕ всех расчётов курса: clGiveAmt = база × (1 + pct/100), поэтому при
 * pct ≠ 0 clGiveAmt × clRate ≠ clGetAmt — осознанно. Профит — от базы.
 */
export const CoefPct = MoneyStr.refine(
  (v) => D(v).gt(-100) && D(v).lte(100),
  { message: 'коэффициент должен быть в диапазоне (−100; 100]' },
);

/**
 * Режим курьерского фикса (courierUsdt — целиком исполнителю):
 *  'extra'    — сверх: клиент доплачивает (clGive += эквивалент, как фикс);
 *  'included' — из суммы: клиент получает меньше (clGet −= эквивалент),
 *               исполнитель удерживает разницу из выдачи (idx2 не растёт).
 * Профит не меняется в обоих режимах.
 */
export const CourierMode = z.enum(['extra', 'included']);
export type CourierMode = z.infer<typeof CourierMode>;

export const OrderDetail = OrderListItem.extend({
  clRate: MoneyStr.nullable(),
  /** Введённый процент коэффициента; null = без коэффициента (и старые заявки). */
  coefPct: MoneyStr.nullable(),
  /** Сумма клиента ДО коэффициента (база расчёта профита). null — старые заявки. */
  clGiveBaseAmt: MoneyStr.nullable(),
  /** Фикс за платёжку в USDT (источник истины). null = без фикса. */
  fixUsdt: MoneyStr.nullable(),
  /** Фикс в валюте клиента (clGiveCcy), заморожен при создании. */
  fixClAmt: MoneyStr.nullable(),
  /** Фикс в валюте исполнителя (exGetCcy), заморожен при создании. */
  fixExAmt: MoneyStr.nullable(),
  /** Бонус исполнителю в USDT (источник истины). null = без бонуса. */
  bonusUsdt: MoneyStr.nullable(),
  /** Бонус в валюте исполнителя (exGetCcy), заморожен при создании. */
  bonusExAmt: MoneyStr.nullable(),
  /** Курьер в USDT (целиком исполнителю). null = без курьера. */
  courierUsdt: MoneyStr.nullable(),
  /** Режим курьера: 'extra' — клиент платит сверх; 'included' — из суммы клиенту. */
  courierMode: CourierMode.nullable(),
  /** Замороженный эквивалент на клиентской стороне (extra → clGiveCcy, included → clGetCcy). */
  courierClAmt: MoneyStr.nullable(),
  /** Замороженный эквивалент в exGetCcy (в included — справочно, в idx2 не влит). */
  courierExAmt: MoneyStr.nullable(),
  exGiveCcy: CcyCodeSchema,
  exGiveAmt: MoneyStr,
  exGetCcy: CcyCodeSchema,
  exGetAmt: MoneyStr,
  exRate: MoneyStr.nullable(),
  /** Причина удаления (soft-delete). null = заявка не удалена. */
  deleteReason: z.string().nullable(),
  /**
   * Снимок курсов USDT на момент создания заявки. Иммутабельный.
   * UI использует для показа «расчёт по курсам на ...» и точной воспроизводимости профита.
   * Формат: { "USD": "1", "RUB": "83.98", ... }
   */
  usdtRatesSnapshot: z.record(CcyCodeSchema, MoneyStr),
  provodki: z.array(Provodka),
  reverseEntries: z.array(ReverseEntry),
  createdAt: z.string(),
  updatedAt: z.string(),
});
export type OrderDetail = z.infer<typeof OrderDetail>;

export const CreateOrderInput = z.object({
  client: AccountCode,
  executor: AccountCode,
  clGiveCcy: CcyCodeSchema,
  /** БАЗОВАЯ сумма клиента (до коэффициента) — сервер умножает сам. */
  clGiveAmt: PositiveMoney,
  /** Коэффициент в процентах; отсутствие/0 = без наценки. */
  coefPct: CoefPct.optional(),
  /**
   * Фикс за выпуск платежа в USDT (pass-through): прибавляется к ноге клиента
   * и ноге исполнителя ПОСЛЕ курсов и коэффициента, конвертируется в валюту
   * каждой ноги по курсу снапшота. В профит не входит. Отсутствие/0 = без фикса.
   */
  fixUsdt: PositiveMoney.optional(),
  /**
   * Бонус исполнителю в USDT (благодарность за сделку): прибавляется ТОЛЬКО к ноге
   * исполнителя (idx2) ПОСЛЕ курсов, коэффициента и фикса, конвертируется в валюту
   * exGetCcy по курсу снапшота. Клиента не касается. Оплачивается из маржи компании
   * → уменьшает профит (нетто = спред − бонус). Отсутствие/0 = без бонуса.
   */
  bonusUsdt: PositiveMoney.optional(),
  /**
   * Курьер в USDT: целиком отходит исполнителю. Режим (courierMode) определяет,
   * кто платит: 'extra' — клиент сверх (clGive += эквивалент по снапшоту);
   * 'included' — из суммы клиенту (сервер уменьшает clGet на эквивалент в
   * clGetCcy, исполнитель удерживает разницу из выдачи). Отсутствие/0 = без курьера.
   */
  courierUsdt: PositiveMoney.optional(),
  /** Обязателен при courierUsdt; без courierUsdt игнорируется. */
  courierMode: CourierMode.optional(),
  clGetCcy: CcyCodeSchema,
  clGetAmt: PositiveMoney,
  clRate: MoneyStr.optional().nullable(),
  exGiveCcy: CcyCodeSchema,
  exGiveAmt: PositiveMoney,
  exGetCcy: CcyCodeSchema,
  exGetAmt: PositiveMoney,
  exRate: MoneyStr.optional().nullable(),
  operator: OperatorUsername,
  date: IsoDate.optional(),
  /**
   * Фактический фикс курсов к USDT для расчёта профита этой заявки.
   * Перекрывает справочник в снапшоте. Ключи — только clGiveCcy/exGetCcy
   * (валюты, участвующие в профите), USDT не перекрывается (всегда 1).
   */
  rateOverrides: z.record(CcyCodeSchema, PositiveMoney).optional(),
});
export type CreateOrderInput = z.infer<typeof CreateOrderInput>;

/** Уточнение фикса курсов профита после создания (до закрытия заявки). */
export const UpdateProfitRatesInput = z.object({
  rates: z.record(CcyCodeSchema, PositiveMoney),
});
export type UpdateProfitRatesInput = z.infer<typeof UpdateProfitRatesInput>;

export const RejectOrderInput = z.object({
  reason: z.string().min(1).max(500),
});
export type RejectOrderInput = z.infer<typeof RejectOrderInput>;

export const DeleteOrderInput = z.object({
  reason: z.string().min(1).max(500),
});
export type DeleteOrderInput = z.infer<typeof DeleteOrderInput>;

export const EditProvodkaInput = z.object({
  account: AccountCode.optional(),
  ccy: CcyCodeSchema.optional(),
  amount: MoneyStr.optional(),
});
export type EditProvodkaInput = z.infer<typeof EditProvodkaInput>;

/**
 * Смена исполнителя в уже созданной заявке. Причина обязательна — попадает в
 * аудит (order_audit action='change-executor'). Разрешено только пока обе ноги
 * исполнителя (idx2/idx3) не подтверждены: подтверждённая нога = деньги реально
 * прошли через прежнего исполнителя, «переклеить» их нельзя.
 */
export const ChangeExecutorInput = z.object({
  executor: AccountCode,
  reason: z.string().min(1).max(500),
});
export type ChangeExecutorInput = z.infer<typeof ChangeExecutorInput>;

/** Запись истории смены исполнителя (проекция order_audit для UI). */
export const ExecutorChangeEntry = z.object({
  id: z.number().int(),
  from: z.string(),
  to: z.string(),
  reason: z.string(),
  /** ISO-время смены. */
  at: z.string(),
  /** Кто сменил — display name пользователя; null для системных/удалённых. */
  by: z.string().nullable(),
});
export type ExecutorChangeEntry = z.infer<typeof ExecutorChangeEntry>;

export const OrdersListQuery = z.object({
  status: z.enum(['all', 'new', 'progress', 'closed', 'reject', 'deleted']).optional(),
  limit: z.string().regex(/^\d+$/).optional(),
  cursor: z.string().optional(),
  /** Поиск по номеру заявки, коду/имени клиента и исполнителя, telegram-заголовку чата. */
  search: z.string().optional(),
});
export type OrdersListQuery = z.infer<typeof OrdersListQuery>;
